import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery")
      .then((res) => {
        setPhotos(res.data.photos || []);
        setCategories(res.data.categories || []);
      })
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(
    () => (activeCategory === "All" ? photos : photos.filter((p) => p.category === activeCategory)),
    [photos, activeCategory]
  );

  const closeLightbox = () => setLightboxIndex(null);
  const step = (dir) =>
    setLightboxIndex((i) => (i === null ? null : (i + dir + visible.length) % visible.length));

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, visible.length]);

  const current = lightboxIndex !== null ? visible[lightboxIndex] : null;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="text-brand font-bold uppercase tracking-widest text-sm">Our Work</span>
          <h2 className="mt-3 font-heading font-bold text-ink text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug">
            Completed Landscaping Projects
          </h2>
          <div className="mt-5 h-1.5 w-24 bg-brand rounded-full" aria-hidden="true" />
          <p className="mt-4 text-[#4B5563] text-lg leading-relaxed">
            Real projects from our crews across the Dallas / DFW metro area. New photos are added regularly.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter projects by category">
          {["All", ...categories].map((cat) => (
            <button
              key={cat}
              type="button"
              data-testid={`gallery-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => { setActiveCategory(cat); setLightboxIndex(null); }}
              aria-pressed={activeCategory === cat}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                activeCategory === cat
                  ? "bg-brand text-white"
                  : "bg-surface text-ink hover:bg-brand-light hover:text-brand"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-12 text-[#4B5563]" data-testid="gallery-loading">Loading project photos…</p>
        ) : visible.length === 0 ? (
          <div
            className="mt-12 border-2 border-dashed border-border rounded-xl p-12 text-center"
            data-testid="gallery-empty-state"
          >
            <ImageOff className="w-10 h-10 text-[#9CA3AF] mx-auto" aria-hidden="true" />
            <p className="mt-4 text-[#4B5563] font-medium">Project photos for this category are coming soon.</p>
            <p className="text-sm text-[#9CA3AF] mt-1">Check back shortly — we add new work regularly.</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
            {visible.map((photo, idx) => (
              <button
                key={photo.id}
                type="button"
                data-testid={`gallery-photo-${photo.id}`}
                onClick={() => setLightboxIndex(idx)}
                className="group relative rounded-xl overflow-hidden border border-border focus:outline-none focus:ring-2 focus:ring-brand text-left"
              >
                <img
                  src={fileUrl(photo.storage_path)}
                  alt={photo.alt || photo.description || "Somoza's Landscaping project photo"}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover group-hover:opacity-90 transition-opacity duration-200"
                />
                <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs px-3 py-2 flex items-center justify-between">
                  <span className="truncate">{photo.description || photo.category}</span>
                  <span className="ml-2 shrink-0 uppercase tracking-wide text-[10px] text-white/80">{photo.category}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {current && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Fullscreen photo viewer"
          data-testid="gallery-lightbox"
          onClick={closeLightbox}
        >
          <button
            type="button"
            data-testid="lightbox-close-button"
            aria-label="Close fullscreen view"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/25 rounded-full p-3 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          {visible.length > 1 && (
            <>
              <button
                type="button"
                data-testid="lightbox-prev-button"
                aria-label="Previous photo"
                onClick={(e) => { e.stopPropagation(); step(-1); }}
                className="absolute left-3 sm:left-6 text-white bg-white/10 hover:bg-white/25 rounded-full p-3 transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                data-testid="lightbox-next-button"
                aria-label="Next photo"
                onClick={(e) => { e.stopPropagation(); step(1); }}
                className="absolute right-3 sm:right-6 text-white bg-white/10 hover:bg-white/25 rounded-full p-3 transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={fileUrl(current.storage_path)}
              alt={current.alt || current.description || "Somoza's Landscaping project photo"}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <figcaption className="mt-3 text-center text-white/90 text-sm" data-testid="lightbox-caption">
              {current.description || current.category}
              <span className="ml-2 text-white/50">({lightboxIndex + 1} of {visible.length})</span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
